import { useState } from "react"
import { Plus } from "@untitledui/icons"
import { toast } from "sonner"

import type { ApiEstimateImage } from "@/lib/api/types"
import {
  ALL_SECTIONS,
  SIDE_OPTIONS,
  type SideBucket,
  componentLabel,
  sideLabel,
} from "@/lib/estimates/catalog"
import { cx } from "@/lib/utils/cx"
import { ComponentPicker } from "./component-picker"
import { PhotoPicker } from "./photo-picker"
import { TextSelect } from "@/components/ui/text-select"
import type { CanvasDispatch, CanvasLine, ComponentPick, LineAction, LinePartType } from "./use-canvas"

// By part: the estimate as a table. One column per fact — what, how,
// which photos, how much — so every row lines up and reads the same.

const usd = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n)

const sectionIdx = (s: string) => {
  const i = ALL_SECTIONS.indexOf(s as (typeof ALL_SECTIONS)[number])
  return i < 0 ? 99 : i
}

export function ByPart({
  images,
  lines,
  dispatch,
}: {
  images: ApiEstimateImage[]
  lines: CanvasLine[]
  dispatch: CanvasDispatch
}) {
  const [adding, setAdding] = useState(false)
  const [photosFor, setPhotosFor] = useState<string | null>(null)
  const photosLine = photosFor ? (lines.find((l) => l.key === photosFor) ?? null) : null

  const byOrder = (a: CanvasLine, b: CanvasLine) =>
    sectionIdx(a.section) - sectionIdx(b.section) || a.component.localeCompare(b.component)
  const groups = [
    { key: "ai", title: "Found by AI", items: lines.filter((l) => l.addedBy === "ai").sort(byOrder) },
    { key: "user", title: "Added by you", items: lines.filter((l) => l.addedBy === "user").sort(byOrder) },
  ].filter((g) => g.items.length > 0)

  const onPick = (pick: ComponentPick) => {
    dispatch({ t: "add", pick, imageId: null })
    setAdding(false)
  }

  return (
    <div>
      <div className="rounded-xl border border-zinc-200 bg-white">
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <h2 className="text-[15px] font-semibold text-zinc-900">
            Parts
            <span className="ml-1.5 font-normal text-zinc-500">· {lines.length}</span>
          </h2>
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-zinc-900 px-3.5 text-[13.5px] font-semibold text-white hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/40 focus-visible:ring-offset-2"
          >
            <Plus className="size-4" aria-hidden />
            Add a part
          </button>
        </div>

        {lines.length === 0 ? (
          <p className="border-t border-zinc-100 px-4 py-8 text-center text-[13.5px] text-zinc-500">Nothing on the estimate yet.</p>
        ) : (
          <div className="overflow-x-auto border-t border-zinc-100">
            <table className="w-full min-w-[960px] table-fixed">
              <colgroup>
                <col />
                <col className="w-[112px]" />
                <col className="w-[116px]" />
                <col className="w-[124px]" />
                <col className="w-[172px]" />
                <col className="w-[152px]" />
                <col className="w-[84px]" />
                <col className="w-[92px]" />
              </colgroup>
              <thead>
                <tr className="border-b border-zinc-100">
                  <Th>Part</Th>
                  <Th>Action</Th>
                  <Th>Part type</Th>
                  <Th>Side</Th>
                  <Th>Photos</Th>
                  <Th>
                    <span className="sr-only">Choose photos</span>
                  </Th>
                  <Th align="right">Price</Th>
                  <Th>
                    <span className="sr-only">Remove</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <GroupRows
                    key={g.key}
                    title={g.title}
                    items={g.items}
                    images={images}
                    dispatch={dispatch}
                    onPhotos={setPhotosFor}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ComponentPicker
        open={adding}
        title="Add a part"
        side="FRONT_CENTER"
        askSide
        lines={lines}
        onPick={onPick}
        onClose={() => setAdding(false)}
      />
      <PhotoPicker line={photosLine} images={images} dispatch={dispatch} onClose={() => setPhotosFor(null)} />
    </div>
  )
}

function GroupRows({
  title,
  items,
  images,
  dispatch,
  onPhotos,
}: {
  title: string
  items: CanvasLine[]
  images: ApiEstimateImage[]
  dispatch: CanvasDispatch
  onPhotos: (key: string) => void
}) {
  return (
    <>
      <tr>
        <td colSpan={8} className="bg-zinc-50/70 px-4 pb-1.5 pt-3 text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-500">
          {title} <span className="font-semibold text-zinc-400">· {items.length}</span>
        </td>
      </tr>
      {items.map((line) => (
        <PartRow key={line.key} line={line} images={images} dispatch={dispatch} onPhotos={() => onPhotos(line.key)} />
      ))}
    </>
  )
}

function PartRow({
  line,
  images,
  dispatch,
  onPhotos,
}: {
  line: CanvasLine
  images: ApiEstimateImage[]
  dispatch: CanvasDispatch
  onPhotos: () => void
}) {
  const label = componentLabel(line.component)
  const shown = images.filter((img) => line.photos.some((p) => p.imageId === img.id))

  return (
    <tr className="border-t border-zinc-100">
      <td className="px-4 py-2.5">
        <p className="truncate text-[14.5px] font-semibold text-zinc-900">{label}</p>
      </td>
      <td className="px-2 py-2.5">
        <TextSelect
          ariaLabel={`${label}: repair or replace`}
          value={line.action}
          onChange={(v) => dispatch({ t: "update", key: line.key, patch: { action: v as LineAction } })}
          options={[
            { value: "REPAIR", label: "Repair" },
            { value: "REPLACE", label: "Replace" },
          ]}
        />
      </td>
      <td className="px-2 py-2.5">
        {line.action === "REPLACE" ? (
          <TextSelect
            ariaLabel={`${label}: part type`}
            value={line.partType}
            onChange={(v) => dispatch({ t: "update", key: line.key, patch: { partType: v as LinePartType } })}
            options={[
              { value: "OEM", label: "OEM" },
              { value: "Aftermarket", label: "Aftermarket" },
            ]}
          />
        ) : (
          <span className="pl-2 text-[13.5px] text-zinc-400">—</span>
        )}
      </td>
      <td className="px-2 py-2.5">
        <TextSelect
          ariaLabel={`${label}: side`}
          value={line.side}
          onChange={(v) => dispatch({ t: "update", key: line.key, patch: { side: v as SideBucket } })}
          options={SIDE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        />
      </td>
      <td className="px-4 py-2.5">
        {shown.length === 0 ? (
          <span className="text-[13px] text-zinc-400">None yet</span>
        ) : (
          <span className="flex items-center gap-1">
            {shown.map((img) => (
              <span key={img.id} className="block h-9 w-12 overflow-hidden rounded-[4px] bg-zinc-100 ring-1 ring-zinc-200" title={sideLabel(img.side)}>
                <img src={img.src} alt={sideLabel(img.side)} className="size-full object-cover" draggable={false} />
              </span>
            ))}
          </span>
        )}
      </td>
      <td className="px-2 py-2.5">
        {images.length > 0 && (
          <button
            type="button"
            onClick={onPhotos}
            className="h-9 whitespace-nowrap rounded-lg border border-zinc-300 bg-white px-3 text-[13px] font-semibold text-zinc-900 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/30"
          >
            Choose photos
          </button>
        )}
      </td>
      <td className="px-2 py-2.5 text-right text-[14.5px] font-semibold tabular-nums text-zinc-900">
        {line.avg != null && line.id != null ? usd(line.avg) : "—"}
      </td>
      <td className="px-2 py-2.5 text-right">
        <button
          type="button"
          onClick={() => {
            dispatch({ t: "remove", key: line.key })
            toast(`${label} removed`, { action: { label: "Undo", onClick: () => dispatch({ t: "restore", line }) } })
          }}
          className={cx(
            "h-9 rounded-md px-2.5 text-[13px] font-semibold text-zinc-600",
            "hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-700/30",
          )}
          aria-label={`Remove ${label} from the estimate`}
        >
          Remove
        </button>
      </td>
    </tr>
  )
}

function Th({ children, align }: { children: React.ReactNode; align?: "right" }) {
  return (
    <th
      scope="col"
      className={cx(
        "px-4 py-2.5 text-left text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-500",
        align === "right" && "text-right",
      )}
    >
      {children}
    </th>
  )
}
